import React, { useCallback, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { PrivacyOption, privacyOptions } from "constants/options"
import { getPrivacyOptionDetail } from "utils/componentsUtils"
import { updateShowCommentsBottomSheet, updateShowLikedByBottomSheet } from "appRedux/slices/wishListSlice"
import { useAppSelector } from "appRedux/hooks"
import { updateFeeds } from "appRedux/slices/feedSlice"
import { userProfileDefaultValue } from "appRedux/slices/userSlice"
import { updateViewedUserProfile } from "appRedux/slices/viewedUserSlice"
import { Badge } from "components/atoms/Badge"
import WishItemReactionBox from "components/molecules/WishItemReactionBox"
import ItemCard from "components/molecules/ItemCard"

interface FeedItemSummaryProps extends FeedItem {
  index: number
}

export default function FeedNewItemSummary(props: FeedItemSummaryProps) {
  const { index, id, name, image, lists, isLiked, likesCount, commentsCount } = props

  const feeds = useAppSelector(state => state.feed.feeds)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const privacyOptionDetails = useMemo(() => {
    const privacyMap = new Map()

    privacyOptions.forEach((option: PrivacyOption) => {
      if (!privacyMap.has(option.key)) {
        privacyMap.set(option.key, getPrivacyOptionDetail(option.key))
      }
    })

    return privacyMap
  }, [])

  const getListBadgeLabel = useCallback((list: FeedItemList) => {
    return (
      <Stack direction="row" gap="4px" justifyContent="center" alignItems="center">
        <PrivacyIconWrapper>
          {privacyOptionDetails.get(list.privacy)?.filledIcon}
        </PrivacyIconWrapper>
        <ListName>{list.name}</ListName>
      </Stack>
    )
  }, [JSON.stringify(privacyOptionDetails)])

  function navigateToList(listId: number) {
    dispatch(updateViewedUserProfile({
      ...userProfileDefaultValue,
      handle: props.user?.handle,
      id: props.user?.id,
      name: props.user?.name,
      avatar: props.user?.avatar || "",
      gender: "MALE",
      isPremium: null,
      isCloseFriend: false
    }))
    navigate(`/user/${props.user?.handle}/list/${listId}`)
  }

  function handleNavigateToItem() {
    navigate(`/user/${props.user?.handle}/item/${id}`)
  }

  function handleLikeButtonClicked() {
    const updatedFeeds = structuredClone(feeds).map((feed: WishamFeed, i: number) => {
      if (feed.type === "NEW_ITEM" && index === i) {
        const itemFeed = feed as NewItemFeed
        itemFeed.item.likesCount = itemFeed.item.isLiked ? itemFeed.item.likesCount - 1 : itemFeed.item.likesCount + 1
        itemFeed.item.isLiked = !itemFeed.item.isLiked
        return itemFeed
      }
      return feed
    })
    dispatch(updateFeeds(updatedFeeds))
  }

  function handleLikeCountClicked() {
    dispatch(updateShowLikedByBottomSheet(true))
    handleNavigateToItem()
  }

  function handleCommentButtonClicked() {
    dispatch(updateShowCommentsBottomSheet(true))
    handleNavigateToItem()
  }

  return (
    <Stack gap="8px">
      <ItemCard name={name} image={image} onItemClick={handleNavigateToItem}>
        <ListsWrapper>
          {lists
            .slice()
            .map((list) => (
              <Badge
                key={list.id}
                label={getListBadgeLabel(list)}
                color={privacyOptionDetails.get(list.privacy)?.palette}
                onClick={() => navigateToList(list.id)}
              />
            ))}
        </ListsWrapper>
      </ItemCard>
      <WishItemReactionBox
        id={id}
        isLiked={isLiked}
        likeCount={likesCount}
        commentCount={commentsCount}
        onLikeButtonClick={handleLikeButtonClicked}
        onLikeCountClick={handleLikeCountClicked}
        onCommentButtonClick={handleCommentButtonClicked}
      />
    </Stack>
  )
}

const ImageContainer = styled(Stack)(({ theme }) => ({
  width: "68px",
  height: "68px",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.gray["8"]}`,
  backgroundColor: theme?.palette.gray["4"],
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  overflow: "hidden",
  backgroundSize: "cover"
}))

const ItemName = styled(Typography)(({ theme }) => ({
  fontSize: "1.2rem",
  fontWeight: 500,
  color: theme.palette.gray["15"]
}))

const ListsWrapper = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: "4px",
  flexWrap: "wrap"
}))

const PrivacyIconWrapper = styled(Stack)(({ theme }) => ({
  flexShrink: 0,
  width: 16,
  height: 16,
  justifyContent: "center",
  alignItems: "center"
}))

const ListName = styled("span")(({ theme }) => ({
  padding: "0 !important",
  color: `${theme.palette.gray["14"]} !important`,
  marginRight: "2px"
}))

